import { DatePipe } from '@angular/common';
import { Injectable } from '@angular/core';
import { ColorMappingItem } from '../model';

import { DateType, TimeDifferenceResult } from '../model/general';

@Injectable({
  providedIn: 'root',
})
export class GeneralService {
  private _pageControlSelectedDate: any;

  constructor(private _datePipe: DatePipe) {}

  /**
   * Gets start end time by date
   * @returns start end time by date
   */
  getStartEndTimeByDate(): [number, number] {
    const startTime = new Date()?.setHours(0, 0, 0, 0);
    const endTime = new Date()?.setHours(23, 59, 59, 999);
    return [startTime, endTime];
  }

  /**
   * Determines whether object is
   * @param value
   * @returns true if object
   */
  isObject(value: any): boolean {
    return value && typeof value === 'object' && value?.constructor === Object;
  }

  /**
   * Determines whether number is
   * @param value
   * @returns true if number
   */
  isNumber(value: any): boolean {
    return value && typeof value === 'number' && Number.isFinite(value);
  }

  /**
   * Gets start end time for week
   * @returns start end time for week
   */
  getStartEndTimeForWeek(): [number, number] {
    const startTime = new Date(
      new Date().getFullYear(),
      new Date().getMonth(),
      new Date().getDate() - 6,
      0,
      0,
      0,
      999
    )?.setHours(0, 0, 0, 0);
    const endTime = new Date()?.setHours(23, 59, 59, 999);
    return [startTime, endTime];
  }

  /**
   * Gets start end time for selected date
   * @param date
   * @returns start end time for selected date
   */
  getStartEndTimeForSelectedDate(
    date: any,
    tenant?: string,
    timeOffset?: number
  ): [number, number] {
    let startTime: number;
    let endTime: number;
    if (tenant === 'VOT') {
      startTime = new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate()
      ).setHours(timeOffset, 0, 0, 0); // timeOffset of the given date
      endTime = new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate() + 1
      ).setHours(timeOffset, 0, 0, 0); // timeOffset of the next day
    } else {
      startTime = new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate()
      ).setHours(0, 0, 0, 0);
      endTime = new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate()
      ).setHours(23, 59, 59, 999);
    }
    return [startTime, endTime];
  }

  /**
   * Gets previous date by type
   * @param type
   * @param [counter]
   * @returns date of previous date by type
   */
  getPreviousDateByType(type: DateType, counter: number = 1): Date {
    const date = new Date();
    switch (type) {
      case 'millisec':
        date?.setMilliseconds(date.getMilliseconds() - counter);
        break;
      case 'sec':
        date?.setSeconds(date.getSeconds() - counter);
        break;
      case 'min':
        date?.setMinutes(date.getMinutes() - counter);
        break;
      case 'hour':
        date?.setHours(date.getHours() - counter);
        break;
      case 'day':
        date?.setDate(date.getDate() - counter);
        break;
      case 'month':
        date?.setMonth(date.getMonth() - counter);
        break;
      case 'year':
        date?.setFullYear(date.getFullYear() - counter);
        break;
    }
    return date;
  }

  /**
   * Formats date
   * @param date
   * @param [format]
   * @param [timezone]
   * @param [locale]
   * @returns date
   */
  formatDate(
    date: any,
    format = 'yyyy-MM-dd',
    timezone?: string,
    locale?: string
  ): string | null {
    return this._datePipe.transform(date, format, timezone, locale);
  }

  /**
   * Calculate time difference between two dates
   * @param baseTime The base time to compare from
   * @param compareTime The time to compare against
   * @returns An object with various time difference representations
   */
  calculateTimeDifference(
    baseTime: string,
    compareTime: string
  ): TimeDifferenceResult {
    // Convert input strings to Date objects
    const base = new Date(baseTime);
    const compare = new Date(compareTime);

    // Calculate difference in milliseconds
    const diffMs = Math.abs(compare.getTime() - base.getTime());

    // Calculate differences
    const seconds = Math.floor(diffMs / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    const months = Math.floor(days / 30);
    const years = Math.floor(months / 12);

    return {
      isInPast: compare < base,
      seconds: seconds % 60,
      minutes: minutes % 60,
      hours: hours % 24,
      days: days % 30,
      months: months % 12,
      years: years,
      totalSeconds: seconds,
      totalMinutes: minutes,
      totalHours: hours,
      totalDays: days,
      totalMonths: months,
      totalYears: years,
    };
  }

  /**
   * Get human-readable time difference
   * @param baseTime The base time to compare from
   * @param compareTime The time to compare against
   * @returns Formatted string representation of time difference
   */
  getHumanReadableTimeDifference(
    baseTime: string,
    compareTime: string
  ): string {
    const diff = this.calculateTimeDifference(baseTime, compareTime);

    // Create an array of time units with their values and labels
    const timeUnits = [
      { value: diff.years, label: diff.years === 1 ? 'yr' : 'yrs' },
      { value: diff.months, label: diff.months === 1 ? 'month' : 'months' },
      { value: diff.days, label: diff.days === 1 ? 'day' : 'days' },
      { value: diff.hours, label: diff.hours === 1 ? 'hr' : 'hrs' },
      { value: diff.minutes, label: diff.minutes === 1 ? 'min' : 'mins' },
      { value: diff.seconds, label: diff.seconds === 1 ? 'sec' : 'secs' },
    ];

    // Filter out zero values
    const nonZeroUnits = timeUnits.filter((unit) => unit.value > 0);

    // If no non-zero units, return the smallest unit
    if (nonZeroUnits.length === 0) {
      return '0 seconds';
    }

    // Take the first two non-zero units
    const formattedUnits = nonZeroUnits
      .slice(0, 2)
      .map((unit) => `${unit.value} ${unit.label}`)
      .join(' ');

    return formattedUnits;
  }

  /**
   * Get concise time difference
   * @param baseTime The base time to compare from
   * @param compareTime The time to compare against
   * @returns Concise formatted string representation
   */
  getConciseTimeDifference(baseTime: string, compareTime: string): string {
    const diff = this.calculateTimeDifference(baseTime, compareTime);

    const timeUnits = [
      { value: diff.years, label: 'y' },
      { value: diff.months, label: 'm' },
      { value: diff.days, label: 'd' },
      { value: diff.hours, label: 'h' },
      { value: diff.minutes, label: 'm' },
      { value: diff.seconds, label: 's' },
    ];

    const nonZeroUnits = timeUnits.filter((unit) => unit.value > 0);

    if (nonZeroUnits.length === 0) {
      return '0s';
    }

    const formattedUnits = nonZeroUnits
      .slice(0, 2)
      .map((unit) => `${unit.value}${unit.label}`)
      .join(' ');

    return formattedUnits;
  }

  /**
   * Method to create color mapping
   * @return
   */
  createColorMapping(
    data: ColorMappingItem[],
    colorType:
      | 'summaryTrackState'
      | 'historyState'
      | 'bgTrackState' = 'summaryTrackState'
  ): Record<number, string> {
    return data?.reduce((acc, item, index) => {
      // Use the original value as the key
      const key = item?.value;
      // Use specified color type with a fallback
      acc[key] = item[colorType] || 'rgba(137, 137, 176, 1)';
      return acc;
    }, {} as Record<number, string>);
  }

  setPageControlDateSelection(date: { startDate: string; endDate: string }) {
    this._pageControlSelectedDate = date;
  }

  getPageControlDateSelection() {
    return this._pageControlSelectedDate;
  }
}
